import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
  TextField,
  Button,
  Chip,
  LinearProgress,
  InputAdornment,
  Tooltip,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import VerifiedIcon from "@mui/icons-material/Verified";
import BlockIcon from "@mui/icons-material/Block";
import TopSection from "../../../../CommonComponents/TopSection";
import ButtonComponent from "../../../../CommonComponents/common/button/Button";
import SectionSpinner from "../../../../CommonComponents/common/Loader/SectionSpinner";
import DomainActionModal from "./DomainActionModal";
import {
  ManageWhitelistDomainProvider,
  useManageWhitelistDomain,
} from "./ManageWhitelistDomainContext";
import {
  manageWhitelistDomainStyles,
  DOMAIN_STATUS_STYLES,
} from "./constants/manageWhitelistDomainStyles";
import {
  MANAGE_WHITELIST_BREADCRUMB,
  PAGE_DESCRIPTION,
  BTN_ADD_DOMAIN,
  BTN_UPLOAD_DOMAIN,
  BTN_WHITELIST,
  BTN_REMOVE_WHITELIST,
  CARD_TOTAL_VENDORS,
  CARD_WHITELISTED,
  CARD_PENDING_WHITELIST,
  CARD_DOMAIN_BLOCKED,
  SEARCH_PLACEHOLDER,
  BTN_CLEAR_FILTERS,
  BTN_DOWNLOAD,
  COL_AL_IDENTIFIER,
  COL_REQUEST_NO,
  COL_DOMAIN_NAME,
  COL_APPLICATION_NAME,
  COL_REQUESTED_BY,
  COL_REQUEST_DATE,
  COL_STATUS,
  getExportFilename,
  PAGE_TITLE,
} from "./constants/manageWhitelistDomainText";

const AdminManageWhitelistDomainInner = () => {
  const {
    rowData,
    summary,
    tableLoading,
    summaryLoading,
    getDomainList,
    getDashboardSummary,
    onUpload,
    addDomain,
    whitelistDomains,
    blacklistDomains,
  } = useManageWhitelistDomain();
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState();
  const [searchText, setSearchText] = useState("");
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [openWhitelistModal, setOpenWhitelistModal] = useState(false);
  const [openBlacklistModal, setOpenBlacklistModal] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openAddDomainModal, setOpenAddDomainModal] = useState(false);
  const loggedInUser = localStorage.getItem("userEmail");

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [10, 25, 50, 100];

  const onGridReady = (params) => {
    setGridApi(params.api);
    gridRef.current = params.api;
  };

  const onFilterChanged = (params) => {
    if (gridRef.current) {
      const filterModel = gridRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0);
    }
  };

  const gridOptions = {
    getRowId: (params) => params.data.domain,
    onFilterChanged,
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agMultiColumnFilter",
      resizable: true,
    }),
    [],
  );

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption("quickFilterText", searchText);
    }
  }, [searchText, gridApi]);

  useEffect(() => {
    getDomainList();
    getDashboardSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = [
    { title: CARD_TOTAL_VENDORS,    value: summary.totalDomains,       percent: 100, color: "#2563eb" },
    { title: CARD_WHITELISTED,      value: summary.domainsWhitelisted,
      percent: summary.totalDomains ? Math.round((summary.domainsWhitelisted / summary.totalDomains) * 100) : 0, color: "#16a34a" },
    { title: CARD_PENDING_WHITELIST, value: summary.pendingWhitelist,
      percent: summary.totalDomains ? Math.round((summary.pendingWhitelist / summary.totalDomains) * 100) : 0, color: "#7c3aed" },
    { title: CARD_DOMAIN_BLOCKED,  value: summary.blockedDomains,
      percent: summary.blockedDomains ? Math.round((0/summary.totalDomains) * 100) : 0, color: "#dc2626" },
  ];

  const canWhitelist =
    selectedDomains.length > 0 &&
    selectedDomains.every((item) => item.status === "UPLOADED");

  const canBlacklist =
    selectedDomains.length > 0 &&
    selectedDomains.every((item) => item.status === "ACTIVE" || item.status === "ONBOARDED");

  const rowSelection = {
    mode: "multiRow",
  };

  const getSafeTicketUrl = (ticketUrl) => {
    if (!ticketUrl) return null;

    try {
      const parsedUrl = new URL(ticketUrl, window.location.origin);
      return ["http:", "https:"].includes(parsedUrl.protocol)
        ? parsedUrl.href
        : null;
    } catch {
      return null;
    }
  };

  const columns = [
    { headerName: COL_AL_IDENTIFIER, field: "alIdentifier", flex: 1.8, sort: "asc" },
    {
      headerName: COL_REQUEST_NO,
      field: "requestNo",
      flex: 1.8,
      cellRenderer: ({ data, value }) => {
        const ticketUrl = getSafeTicketUrl(data?.ticketUrl);

        if (!ticketUrl) return value || "";

        return (
          <Link
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            sx={{ fontWeight: 600 }}
          >
            {value}
          </Link>
        );
      },
    },
    { headerName: COL_DOMAIN_NAME,   field: "domain",       flex: 2 },
    { headerName: COL_APPLICATION_NAME, field: "applicationType", flex: 2 },
    {
      field: "requestedBy",
      headerName: COL_REQUESTED_BY,
      flex: 1.8,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: ({ data }) => (
        <Box sx={manageWhitelistDomainStyles.requestedByCell}>
          <Typography sx={manageWhitelistDomainStyles.requestedByName}>{data.requestedBy}</Typography>
          <Typography sx={manageWhitelistDomainStyles.requestedByEmail}>{data.email}</Typography>
        </Box>
      ),
    },
    {
      field: "requestDate",
      headerName: COL_REQUEST_DATE,
      flex: 1.8,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: ({ data }) => (
        <Box>
          <Typography sx={manageWhitelistDomainStyles.requestDateText}>{data.requestDate}</Typography>
        </Box>
      ),
    },
    {
      headerName: COL_STATUS,
      field: "status",
      flex: 1,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: ({ value }) => {
        const statusStyles = DOMAIN_STATUS_STYLES[value] || {};
        return (
          <Tooltip title={value} placement="top">
            <Chip
              label={value}
              size="small"
              sx={{ ...manageWhitelistDomainStyles.statusChip, ...statusStyles }}
            />
          </Tooltip>
        );
      },
    },
  ];
  
  const handleClearFilters = () => {
    if (gridRef.current) {
      gridRef.current.setFilterModel(null);
    }
  };

  const handleExport = () => {
    if (!gridApi) return;

    const selectedRows = gridApi.getSelectedRows();

    gridApi.exportDataAsCsv({
      fileName: getExportFilename(),
      columnKeys: [
        "alIdentifier",
        "requestNo",
        "domain",
        "applicationType",
        "requestedBy",
        "email",
        "requestDate",
        "status",
      ],
      onlySelected: selectedRows.length > 0,
    });
  };

  const handleUpload = (file) => {
    setUploadOpen(false);
    onUpload(file);
  };

  const handleSubmitAddDomainModal = (data) => {
    handleCloseAddDomainModal();
    addDomain(data);
  };

  const onClickAddDomain = () => {
    setOpenAddDomainModal(true)
  }

  const handleCloseAddDomainModal = () => {
    setOpenAddDomainModal(false);
  }

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <TopSection
        breadcrumbArray={MANAGE_WHITELIST_BREADCRUMB}
        hideHeader={true}
        headerName=""
        hideTitle={true}
      />

      <>
        <Typography variant="h6" fontWeight={700} color="#000" mb={1}>
          {PAGE_TITLE}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography color="text.secondary">
            {PAGE_DESCRIPTION}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <ButtonComponent
              type="secondary"
              maxWidth="200px"
              label={BTN_ADD_DOMAIN}
              height="40px"
              onClick={onClickAddDomain}
            />

            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadOpen(true)}
              sx={{
                color: "#1976d2",
                borderColor: "#1976d2",
                "&:hover": {
                  color: "#1976d2",
                  borderColor: "#1976d2",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
              }}
            >
              {BTN_UPLOAD_DOMAIN}
            </Button>

            <Button
              variant="contained"
              disabled={!canWhitelist}
              startIcon={<VerifiedIcon />}
              onClick={() => setOpenWhitelistModal(true)}
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  boxShadow: "none",
                },
              }}
            >
              {BTN_WHITELIST}
            </Button>

            <Button
              variant="contained"
              disabled={!canBlacklist}
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setOpenBlacklistModal(true)}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  boxShadow: "none",
                },
              }}
            >
              {BTN_REMOVE_WHITELIST}
            </Button>
          </Stack>
        </Box>
        <Grid container spacing={1.5} mb={2.5}>
          {(cards ?? []).map((item) => (
            <Grid item xs={12} md={3} key={item.title}>
              <Card sx={manageWhitelistDomainStyles.summaryCard}>
                <CardContent sx={manageWhitelistDomainStyles.summaryCardContent}>
                  <Typography sx={manageWhitelistDomainStyles.summaryCardTitle}>
                    {item.title}
                  </Typography>

                  {summaryLoading ? (
                    <>
                      <Skeleton variant="text" width="60%" height={42} sx={{ mt: 0.25 }} />
                      <LinearProgress sx={manageWhitelistDomainStyles.skeletonProgressBar} />
                      <Skeleton variant="text" width="35%" height={22} sx={{ mt: 0.5 }} />
                    </>
                  ) : (
                    <>
                      <Typography
                        sx={manageWhitelistDomainStyles.summaryCardValue}
                        color={item.color}
                      >
                        {item.value}
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={item.percent}
                        sx={manageWhitelistDomainStyles.summaryProgressBar}
                      />

                      <Typography sx={manageWhitelistDomainStyles.summaryCardPercent}>
                        {item.percent}%
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={manageWhitelistDomainStyles.gridToolbarRow}>
          <TextField
            size="small"
            placeholder={SEARCH_PLACEHOLDER}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={manageWhitelistDomainStyles.searchField}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={disableFilterBtn}
              startIcon={<FilterAltOffIcon />}
              onClick={handleClearFilters}
              sx={manageWhitelistDomainStyles.outlinedBlueButton}
            >
              {BTN_CLEAR_FILTERS}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={manageWhitelistDomainStyles.outlinedBlueButton}
            >
              {BTN_DOWNLOAD}
            </Button>
          </Stack>
        </Box>
        <Box sx={manageWhitelistDomainStyles.gridWrapper}>
          <div
            className="ag-theme-quartz"
            style={{ height: 400, width: "100%" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              rowSelection={rowSelection}
              onSelectionChanged={(params) => {
                setSelectedDomains(params.api.getSelectedRows());
              }}
            />
          </div>
          {tableLoading && <SectionSpinner />}
        </Box>
        <DomainActionModal
          action="upload"
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUpload={handleUpload}
        />
        <DomainActionModal
          action="whitelist"
          open={openWhitelistModal}
          onClose={() => setOpenWhitelistModal(false)}
          domains={selectedDomains}
          onSubmit={(payload) => {
            setOpenWhitelistModal(false);
            setSelectedDomains([]);
            gridRef.current?.deselectAll();
            whitelistDomains(payload);
          }}
        />
        <DomainActionModal
          action="blacklist"
          open={openBlacklistModal}
          onClose={() => setOpenBlacklistModal(false)}
          domains={selectedDomains}
          requestedBy={loggedInUser}
          onSubmit={(payload) => {
            setOpenBlacklistModal(false);
            setSelectedDomains([]);
            gridRef.current?.deselectAll();
            blacklistDomains(payload);
          }}
        />
        <DomainActionModal
          action="add"
          open={openAddDomainModal}
          onClose={handleCloseAddDomainModal}
          onSubmit={handleSubmitAddDomainModal}
        />
      </>
    </Box>
  );
};

const AdminManageWhitelistDomain = () => (
  <ManageWhitelistDomainProvider>
    <AdminManageWhitelistDomainInner />
  </ManageWhitelistDomainProvider>
);

export default AdminManageWhitelistDomain;
